/* eslint-disable @typescript-eslint/no-misused-promises */
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, Box, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import character from '../../assets/images/intake/step2/char.svg';
import { useUserAsaTaskHistoryContext } from '../../hooks/use-user-asa-task-history-context.hooks';
import { useAuthContext } from '../../hooks/use-user-auth-context.hooks';
import {
  AsaTaskName,
  AsaUserTasks,
  completeASATask,
  dispatchUpdateAsaTaskHistoryInfo,
} from '../../redux/slice/asa-task-history';
import { CustomUser } from '../../redux/slice/auth/CustomUser';
import {
  CareerRoadMapDto,
  MotivationDto,
} from '../../redux/slice/career-roadmap/career-roadmap-type';
import { dispatchCareerRoadMapAssessment } from '../../redux/slice/career-roadmap/career-roadmap.operations';
import { roadMapQuestions } from '../../redux/slice/career-roadmap/careerRoadMapQuestions';
import { RootState, useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';
import { EnvironmentVariable } from '../../utils/constants';
import { buildASANextTaskUrl } from '../../utils/helper';
import { CircularLoader } from '../loader/CircularLoader';

interface ComponentProps {
  step: number;
  onBack: () => void;
}

interface FormData extends MotivationDto {}

const CareerRoadMapSchema = yup
  .object({
    motivation: yup.string().required('Please select a option'),
    workStyle: yup.string().required('Please select a option'),
  })
  .required();

export const Motivation: React.FC<ComponentProps> = ({ step, onBack }) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const motivationState = useSelector((state: RootState) => state.careerRoadmap.motivation_vector);
  const workStyleState = useSelector((state: RootState) => state.careerRoadmap.workstyle_vector);

  const currentUser: CustomUser | null = useAuthContext();
  const [currsentAsaTask, setCurrentAsaTask] = useState<AsaUserTasks | undefined>(undefined);
  const asaUserTaskHistory = useUserAsaTaskHistoryContext();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(CareerRoadMapSchema),
    defaultValues: {
      motivation: motivationState?.answer || '',
      workStyle: workStyleState?.answer || '',
    },
  });

  const backButtonClick = () => {
    onBack();
  };

  useEffect(() => {
    if (
      currentUser?.claims?.isAsaUser &&
      Object.keys(asaUserTaskHistory).length &&
      asaUserTaskHistory?.tasks?.length
    ) {
      const currentTask = asaUserTaskHistory.tasks?.find(
        (x) => !x.isCompleted && x.taskName === AsaTaskName.CAREERROADMAP,
      );

      setCurrentAsaTask(currentTask);

      if (currentTask && !currentTask?.startDate) {
        void dispatch(
          dispatchUpdateAsaTaskHistoryInfo({
            asaUserId: currentUser?.claims?.asaUserId || 0,
            taskId: currentTask?.taskId || '',
            taskStep: currentTask?.taskStep || 1,
            startDate: currentTask?.startDate || new Date(),
            taskName: AsaTaskName.CAREERROADMAP,
          }),
        );
      }

      if (currsentAsaTask && (currsentAsaTask?.taskStep as number) != 3) {
        if (currsentAsaTask?.taskUrl) {
          const url = new URL(currsentAsaTask?.taskUrl || '');
          navigate(`${url.pathname}${url.search}`);
          // setIsDisabled(true);
        }
      }
    }
  }, [asaUserTaskHistory, currentUser, currsentAsaTask]);

  const submitCareerRoadMapAnswer: SubmitHandler<FormData> = async (data: FormData) => {
    logger.debug('Submitted personal info.', data);
    setIsLoading(true);

    try {
      if (data) {
        const question2: CareerRoadMapDto = {
          id: roadMapQuestions[1].questionNumber,
          answer: data?.motivation,
          question: roadMapQuestions[1].question,
        };
        if (!motivationState?.answer || motivationState?.answer !== data.motivation) {
          question2 &&
            (await dispatch(dispatchCareerRoadMapAssessment(question2, 'motivation_vector')));
        }

        const question3: CareerRoadMapDto = {
          id: roadMapQuestions[2].questionNumber,
          answer: data.workStyle,
          question: roadMapQuestions[2].question,
        };

        if (!workStyleState?.answer || workStyleState.answer !== data.workStyle) {
          question3 &&
            (await dispatch(dispatchCareerRoadMapAssessment(question3, 'workstyle_vector')));
        }
        if (currentUser?.claims?.isAsaUser && currsentAsaTask) {
          const endPointUrl = process.env[EnvironmentVariable.REACT_APP_ASA_COMPLETE_TASK_URL];
          const isStaging =
            process.env[EnvironmentVariable.REACT_APP_BASE_URL]?.includes('staging') ||
            process.env[EnvironmentVariable.REACT_APP_BASE_URL]?.includes('localhost');
          const taskResponse = await completeASATask(
            {
              asaUserId: currentUser?.claims?.asaUserId as number,
              taskId: currsentAsaTask?.taskId,
              taskStep: currsentAsaTask?.taskStep || step,
            },
            endPointUrl || '',
            isStaging || false,
          );

          if (!taskResponse || !taskResponse?.ok) {
            toast.error('Something went wrong!, Please try after sometime.');
            return;
          }

          let url = new URL(currsentAsaTask?.taskUrl || '');
          const nextTaskUrl = buildASANextTaskUrl(url, AsaTaskName.CAREERROADMAP, step + 1);
          url = new URL(nextTaskUrl || '');
          await dispatch(
            dispatchUpdateAsaTaskHistoryInfo({
              startDate: new Date(),
              asaUserId: currentUser?.claims?.asaUserId || 0,
              taskId: currsentAsaTask?.taskId || '',
              taskStep:
                (currsentAsaTask && parseInt(currsentAsaTask?.taskStep.toString()) + 1) || 3,
              taskUrl: nextTaskUrl,
              taskName: AsaTaskName.CAREERROADMAP,
              totalSteps: 17,
            }),
          );

          await dispatch(
            dispatchUpdateAsaTaskHistoryInfo({
              asaUserId: currentUser?.claims?.asaUserId || 0,
              taskId: currsentAsaTask?.taskId || '',
              taskStep: currsentAsaTask?.taskStep || step,
              isCompleted: true,
              completedDate: new Date(),
            }),
          );
          toast.success('Task has been successfully submitted');
          navigate(`${url.pathname}${url.search}`);
        } else {
          navigate(`/career-roadmap/step${step + 1}`);
        }
      }
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    reset({
      motivation: motivationState?.answer,
      workStyle: workStyleState?.answer,
    });
  }, [reset, motivationState?.answer, workStyleState?.answer]);

  useEffect(() => {
    window.scrollTo(0, 0);
  });

  return (
    <div>
      {isLoading && <CircularLoader />}

      <div className="character absolute bottom-0 right-[-85px] lg:right-[-138px]">
        <img className="" src={character} alt="" />
      </div>
      <div className="flex mt-9 mb-10 md:px-10">
        <Box
          className="w-full"
          component="form"
          sx={{ '& > :not(style)': { mb: 4 } }}
          autoComplete="off"
          onSubmit={handleSubmit(submitCareerRoadMapAnswer)}
        >
          <div className="flex mt-9 mb-10 ">
            <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">
              {roadMapQuestions[1].questionNumber}
            </div>
            <div>
              <h4 className="text-2xl">
                <span className="font-semibold">{roadMapQuestions[1].question} </span>
              </h4>
            </div>
          </div>
          <Controller
            control={control}
            name="motivation"
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                freeSolo
                options={roadMapQuestions[1]?.options || []}
                value={value || ''}
                onChange={(_, newValue) => onChange(newValue)}
                onInputChange={(_, newInputValue) => onChange(newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Motivation"
                    placeholder="Type or select an option..."
                    required
                    error={!!errors.motivation}
                    helperText={
                      errors.motivation?.message ||
                      'You may select from the list or type your own answer'
                    }
                  />
                )}
              />
            )}
          />
          <div className="flex mt-9 mb-10 ">
            <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">
              {roadMapQuestions[2].questionNumber}
            </div>
            <div>
              <h4 className="text-2xl">
                <span className="font-semibold">{roadMapQuestions[2].question} </span>
              </h4>
            </div>
          </div>
          <Controller
            control={control}
            name="workStyle"
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                freeSolo
                options={roadMapQuestions[2]?.options || []}
                value={value || ''}
                onChange={(_, newValue) => onChange(newValue)}
                onInputChange={(_, newInputValue) => onChange(newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Work-Style"
                    placeholder="Type or select an option..."
                    required
                    error={!!errors.workStyle}
                    helperText={
                      errors.workStyle?.message ||
                      'You may select from the list or type your own answer'
                    }
                  />
                )}
              />
            )}
          />

          <div className="flex justify-end lg:justify-between lg:mt-20 mt-10">
            <button onClick={handleSubmit(backButtonClick)} className="btn-internal" type="button">
              Back
            </button>
            <button className="btn-internal ml-5" disabled={isLoading} type="submit">
              Next
            </button>
          </div>
        </Box>
      </div>
    </div>
  );
};
